import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { CircularProgress, LinearProgress } from '@mui/material'
import AssessmentIcon from '@mui/icons-material/Assessment'
import SearchIcon from '@mui/icons-material/Search'
import DescriptionIcon from '@mui/icons-material/Description'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

const STAGE_STEP = { searching: 1, analysing: 2, compiling: 3 }

const ReportLoadingState = ({ reportVersion, status, reportTitle, liveProgress }) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const hasLiveProgress = !!liveProgress?.stage

  const steps = [
    {
      id: 'init',
      label: 'Initializing report generation',
      description: 'Preparing analysis environment',
      icon: <AssessmentIcon />
    },
    {
      id: 'search',
      label: 'Searching building documents',
      description: 'Scanning all uploaded files',
      icon: <SearchIcon />
    },
    {
      id: 'analyze',
      label: 'Analysing documents with AI',
      description: 'Processing report content',
      icon: <AutoAwesomeIcon />
    },
    {
      id: 'compile',
      label: 'Compiling answers and sources',
      description: 'Finalising report with citations',
      icon: <DescriptionIcon />
    }
  ]

  useEffect(() => {
    if (!hasLiveProgress) return
    const stage = liveProgress.stage
    const total = liveProgress.total || 0
    const done = Math.min(liveProgress.done || 0, total)
    setActiveStepIndex(STAGE_STEP[stage] ?? 1)
    if (stage === 'searching') {
      setProgress(12)
    } else if (stage === 'analysing') {
      setProgress(total > 0 ? 20 + (done / total) * 65 : 20)
    } else if (stage === 'compiling') {
      setProgress(92)
    }
  }, [hasLiveProgress, liveProgress?.stage, liveProgress?.done, liveProgress?.total])

  // Simulated fallback for reports still generating without progress data
  useEffect(() => {
    if (hasLiveProgress) return
    if (status === 'pending') {
      setActiveStepIndex(0)
      setProgress(0)
    } else if (status === 'processing') {
      const stepInterval = setInterval(() => {
        setActiveStepIndex(prev => {
          if (prev < 2) return prev + 1
          return prev
        })
      }, 45000)

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev < 85) return prev + Math.random() * 0.6
          return prev
        })
      }, 1000)

      return () => {
        clearInterval(stepInterval)
        clearInterval(progressInterval)
      }
    }
  }, [status, hasLiveProgress])

  return (
    <LoadingContainer>
      <BackgroundGlow />

      <HeaderSection>
        <SpinnerContainer>
          <CircularProgress
            size={80}
            thickness={2}
            sx={{ color: '#33becc' }}
          />
          <IconOverlay>
            <AssessmentIcon />
          </IconOverlay>
        </SpinnerContainer>

        <Title>Generating {reportTitle || 'Report'}</Title>

        {reportVersion && (
          <VersionBadge>Version {reportVersion}</VersionBadge>
        )}

        <Subtitle>
          Analysing your building documents and preparing your report...
        </Subtitle>

        <ProgressSection>
          <ProgressHeader>
            <ProgressLabel>Overall Progress</ProgressLabel>
            <ProgressValue>{Math.round(progress)}%</ProgressValue>
          </ProgressHeader>
          <MainProgressBar>
            <MainProgressFill progress={progress} />
          </MainProgressBar>
        </ProgressSection>
      </HeaderSection>

      <StepsContainer>
        {steps.map((step, index) => {
          const isActive = index === activeStepIndex
          const isCompleted = index < activeStepIndex
          const isPending = index > activeStepIndex

          return (
            <StepItem
              key={step.id}
              isActive={isActive}
              isCompleted={isCompleted}
              isPending={isPending}
            >
              <StepIconWrapper isActive={isActive} isCompleted={isCompleted}>
                {isCompleted ? <CheckCircleIcon /> : step.icon}
              </StepIconWrapper>

              <StepContent>
                <StepLabel isActive={isActive} isCompleted={isCompleted}>
                  {step.label}
                </StepLabel>
                <StepDescription isActive={isActive} isPending={isPending}>
                  {step.id === 'analyze' && hasLiveProgress && liveProgress.stage === 'analysing' && liveProgress.total > 0
                    ? `Section ${Math.min((liveProgress.done || 0) + 1, liveProgress.total)} of ${liveProgress.total} — processing report content`
                    : step.description}
                </StepDescription>
              </StepContent>

              {isActive && (
                <ActiveIndicator>
                  <LoadingDot delay={0} />
                  <LoadingDot delay={0.2} />
                  <LoadingDot delay={0.4} />
                </ActiveIndicator>
              )}

              {isCompleted && (
                <CompletedBadge>Done</CompletedBadge>
              )}

              {isActive && (
                <StepProgressBar>
                  <LinearProgress
                    variant="indeterminate"
                    sx={{
                      height: 3,
                      borderRadius: 1.5,
                      backgroundColor: 'transparent',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 1.5,
                      }
                    }}
                  />
                </StepProgressBar>
              )}
            </StepItem>
          )
        })}
      </StepsContainer>

      <InfoSection>
        <InfoCard>
          <InfoIcon>
            <AutoAwesomeIcon />
          </InfoIcon>
          <InfoContent>
            <InfoTitle>What's happening?</InfoTitle>
            <InfoText>
              Analysing all your building documents using advanced AI
              to generate your report with source citations.
            </InfoText>
          </InfoContent>
        </InfoCard>

        <TimeEstimate>
          <TimeIcon>🕐</TimeIcon>
          <TimeText>
            <strong>Estimated time:</strong> usually 5–15 minutes depending on the report type and how many documents your building has
          </TimeText>
        </TimeEstimate>

        <NavigateHint>
          You can navigate away — generation continues in the background and we'll notify you when the report is ready.
        </NavigateHint>
      </InfoSection>
    </LoadingContainer>
  )
}

export default ReportLoadingState

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.3);
  }
`

const glow = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
`

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
  position: relative;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 600px) {
    padding: 32px 16px;
  }
`

const BackgroundGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  height: 600px;
  background: radial-gradient(
    circle,
    ${({ theme }) => theme.colors.primary}15 0%,
    transparent 70%
  );
  animation: ${glow} 3s ease-in-out infinite;
  pointer-events: none;

  @media (max-width: 600px) {
    width: 400px;
    height: 400px;
  }
`

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 40px;
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    margin-bottom: 32px;
  }
`

const SpinnerContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
`

const IconOverlay = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 14px;
  color: white;
  box-shadow: 0 4px 16px rgba(210, 71, 36, 0.3);

  svg {
    font-size: 26px;
  }
`

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 12px;

  @media (max-width: 600px) {
    font-size: 22px;
    margin-bottom: 8px;
  }
`

const VersionBadge = styled.span`
  display: inline-block;
  padding: 6px 16px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}15 0%, ${({ theme }) => theme.colors.primary}08 100%);
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid ${({ theme }) => theme.colors.primary}20;
`

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.grey[200]};
  max-width: 440px;
  margin: 0 0 28px;
  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 14px;
    margin-bottom: 24px;
  }
`

const ProgressSection = styled.div`
  width: 100%;
  max-width: 400px;
`

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const ProgressLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const ProgressValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`

const MainProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 4px;
  overflow: hidden;
`

const MainProgressFill = styled.div`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary} 0%, #B83D1F 100%);
  border-radius: 4px;
  transition: width 0.5s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
  }
`

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 480px;
  margin-bottom: 36px;
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    gap: 10px;
  }
`

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: ${({ isActive, isCompleted }) =>
    isActive ? 'linear-gradient(135deg, rgba(210, 71, 36, 0.1) 0%, rgba(210, 71, 36, 0.05) 100%)' :
    isCompleted ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)' :
    '#ffffff'};
  border: 1px solid ${({ isActive, isCompleted, theme }) =>
    isActive ? `${theme.colors.primary}40` :
    isCompleted ? '#10B98140' :
    theme.colors.lightGrey};
  border-radius: 14px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: ${({ isPending }) => isPending ? 0.6 : 1};

  @media (max-width: 600px) {
    padding: 14px 16px;
    gap: 12px;
  }
`

const StepIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: ${({ isActive, isCompleted, theme }) =>
    isActive ? `${theme.colors.primary}20` :
    isCompleted ? '#D1FAE5' :
    '#F3F4F6'};
  border-radius: 12px;
  color: ${({ isActive, isCompleted, theme }) =>
    isActive ? theme.colors.primary :
    isCompleted ? '#10B981' :
    theme.colors.grey[200]};
  flex-shrink: 0;
  transition: all 0.3s ease;

  svg {
    font-size: 22px;
  }

  @media (max-width: 600px) {
    width: 40px;
    height: 40px;

    svg {
      font-size: 20px;
    }
  }
`

const StepContent = styled.div`
  flex: 1;
  text-align: left;
  min-width: 0;
`

const StepLabel = styled.span`
  display: block;
  font-size: 15px;
  font-weight: ${({ isActive }) => isActive ? 600 : 500};
  color: ${({ isActive, isCompleted, theme }) =>
    isActive ? theme.colors.haiti :
    isCompleted ? theme.colors.haiti :
    theme.colors.grey[200]};

  @media (max-width: 600px) {
    font-size: 14px;
  }
`

const StepDescription = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-top: 2px;
  opacity: ${({ isPending }) => isPending ? 0.7 : 1};
`

const ActiveIndicator = styled.div`
  display: flex;
  gap: 4px;
  margin-left: auto;
  padding-left: 12px;
`

const LoadingDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay}s;
`

const CompletedBadge = styled.span`
  margin-left: auto;
  padding: 4px 12px;
  background: #D1FAE5;
  color: #10B981;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`

const StepProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
`

const InfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 22px;
  background: linear-gradient(to right, #ffffff, #fafafa);
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 14px;
  max-width: 440px;
  text-align: left;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 600px) {
    padding: 16px 18px;
    gap: 12px;
  }
`

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.secondary}15;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.secondary};
  flex-shrink: 0;

  svg {
    font-size: 22px;
  }
`

const InfoContent = styled.div`
  flex: 1;
`

const InfoTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 4px;
`

const InfoText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin: 0;
  line-height: 1.5;
`

const TimeEstimate = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.primary}08;
  border-radius: 24px;
`

const TimeIcon = styled.span`
  font-size: 16px;
`

const TimeText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};

  strong {
    font-weight: 600;
  }
`

const NavigateHint = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin: 0;
  font-style: italic;
`
